import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";

import { useToast } from "src/context/ToastContext";
import { CriteriaClassificationEntity } from "src/entities/CriteriaClassificationEntity";
import CriteriaClassificationService, { criteriaClassificationQueryKeys } from "src/services/CriteriaClassificationService";
import Navigation from "./Navigation";
import { clearCriteriaStepDraft, getCriteriaStepDraft, setCriteriaStepDraft } from "./draftStorage";

const criteriaClassificationService = new CriteriaClassificationService();

type ClassificationItem = {
	id?: number;
	title: string;
	originalTitle?: string;
};

type StepClassificationsDraft = {
	classifications: ClassificationItem[];
	removedIds: number[];
};

type StepClassificationsProps = {
	criteriaId: number;
	backLabel: string;
	nextLabel: string;
	onBack: () => void;
	onComplete: () => void;
};

export default function StepClassifications({ criteriaId, backLabel, nextLabel, onBack, onComplete }: StepClassificationsProps) {
	const { handleToast } = useToast();
	const draftRef = useRef(getCriteriaStepDraft<StepClassificationsDraft>(criteriaId, "classifications"));
	const [classifications, setClassifications] = useState<ClassificationItem[]>(() => draftRef.current?.classifications ?? []);
	const [removedIds, setRemovedIds] = useState<number[]>(() => draftRef.current?.removedIds ?? []);

	const { data: queryData, isLoading } = useQuery<CriteriaClassificationEntity[]>(
		criteriaClassificationQueryKeys.classifications(String(criteriaId)),
		() => criteriaClassificationService.getClassifications(criteriaId).then(response => response.data.classifications),
		{ refetchOnWindowFocus: false }
	);

	useEffect(() => {
		if (!draftRef.current && queryData) {
			setClassifications(queryData.map(classification => ({ 
				id: classification.id, 
				title: classification.title, 
				originalTitle: classification.title 
			})));
		}
	}, [queryData]);

	const mutation = useMutation(
		async () => {
			const created = classifications.filter(classification => !classification.id && classification.title.trim());
			const updated = classifications.filter(classification => classification.id && classification.title !== classification.originalTitle);
			const calls: Promise<unknown>[] = [];

			if (created.length > 0) {
				calls.push(criteriaClassificationService.postClassifications({ criteriaId, classifications: created.map(classification => ({ title: classification.title })) }));
			}

			if (updated.length > 0) {
				calls.push(criteriaClassificationService.putClassifications({ classifications: updated.map(classification => ({ id: classification.id!, title: classification.title })) }));
			}

			if (removedIds.length > 0) {
				calls.push(criteriaClassificationService.deleteClassifications({ criteriaId, classifications: removedIds }));
			}

			await Promise.all(calls);
		},
		{
			onSuccess: () => {
				clearCriteriaStepDraft(criteriaId, "classifications");
				onComplete();
			},
			onError: (err: AxiosError) => {
				handleToast("Critério", `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	function handleAdd() {
		setClassifications(prev => [...prev, { title: "" }]);
	}

	function handleNext() {
		if (classifications.length === 0) {
			handleToast("Critério", "É necessário adicionar pelo menos uma classificação para continuar.", 5000, "danger");
			return;
		}
		mutation.mutate();
	}

	function handleTitleChange(index: number, value: string) {
		setClassifications(prev => prev.map((classification, idx) => idx === index ? { ...classification, title: value } : classification));
	}

	function handleRemove(index: number) {
		const item = classifications[index];
		
		if (item.id) {
			setRemovedIds(prev => [...prev, item.id!]);
		}

		setClassifications(prev => prev.filter((_, idx) => idx !== index));
	}

	function handleBack() {
		setCriteriaStepDraft(criteriaId, "classifications", { classifications, removedIds });
		onBack();
	}

	return (
		<>
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Classificações
				</Card.Header>
				<Card.Body>
					{isLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<Row key={skeletonIndex} className="mb-3 align-items-end">
								<Col>
									<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
									<div className="skeleton skeleton-button" style={{ height: 38 }} />
								</Col>
								<Col xs="auto" className="d-flex align-items-end">
									<div className="skeleton skeleton-button" style={{ height: 38, width: 44 }} />
								</Col>
							</Row>
						))
					) : classifications.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						classifications.map((item, index) => (
							<Row key={index} className="mb-3 align-items-end">
								<Col>
									<Form.Label className="text-muted"><small>Título *</small></Form.Label>
									<Form.Control
										type="text"
										placeholder="Informe aqui"
										required
										value={item.title}
										onChange={(e) => handleTitleChange(index, e.target.value)}
									/>
								</Col>
								<Col xs="auto" className="d-flex align-items-end">
									<Button variant="light" className="rounded-3 shadow-sm" type="button" onClick={() => handleRemove(index)}>
										<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
									</Button>
								</Col>
							</Row>
						))
					)}
				</Card.Body>
				<Card.Footer className="bg-white">
					<Button
						variant="light"
						className="d-flex rounded-3 shadow-sm"
						type="button"
						onClick={handleAdd}
						disabled={isLoading}
					>
						<Icon path={mdiPlus} size={1} /> Adicionar Classificação
					</Button>
				</Card.Footer>
			</Card>
			<Navigation
				backLabel={backLabel}
				nextLabel={nextLabel}
				saving={mutation.isLoading}
				onBack={handleBack}
				onNext={handleNext}
			/>
		</>
	);
}
